"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateOrderDiscountAmount = exports.calculateTicketLineDiscount = exports.calculateOrderFullOrderDiscount = exports.calculateTotalOrderAmount = exports.calculateTotalDiscountForOrder = exports.divideAmountAcrossParts = exports.calculateGroupTicketDiscount = exports.calculateSingleAttendeeDiscount = exports.calculateWhenPerTicketDiscount = exports.allocateFullOrderDiscount = exports.calculateDiscountPerProductTicket = void 0;
const ticketing_1 = require("../enums/ticketing");
const discount_helpers_1 = require("./discount-helpers");
const calculateDiscountPerProductTicket = (params) => {
    const { discount, rate } = params;
    if (rate.isPayWhatYouCan) {
        return { discountId: null, discountAmount: 0 };
    }
    if (!discount) {
        return { discountId: null, discountAmount: 0 };
    }
    if (discount.scope === ticketing_1.DiscountScope.FullOrder) {
        return calculateWhenFullOrderDiscount(params);
    }
    return (0, exports.calculateWhenPerTicketDiscount)(params);
};
exports.calculateDiscountPerProductTicket = calculateDiscountPerProductTicket;
const calculateWhenFullOrderDiscount = (params) => {
    const { discount, fullOrderUnitDiscountAmount = 0, rate, attendeeIndex = 0 } = params;
    if (rate.isPayWhatYouCan) {
        return { discountId: null, discountAmount: 0 };
    }
    const attendeeCount = rate.attendeesByTicket || 1;
    if (attendeeCount === 1) {
        return { discountId: discount.id, discountAmount: fullOrderUnitDiscountAmount };
    }
    return {
        discountId: discount.id,
        discountAmount: (0, exports.divideAmountAcrossParts)(fullOrderUnitDiscountAmount, attendeeCount, attendeeIndex),
    };
};
const allocateFullOrderDiscount = (discount, unitAmounts) => {
    const totalAmount = unitAmounts.reduce((sum, amount) => sum + amount, 0);
    if (totalAmount === 0) {
        return unitAmounts.map(() => 0);
    }
    if (discount.type === ticketing_1.DiscountType.Percentage) {
        return allocateProRata(Math.floor((totalAmount * discount.amount) / 100), unitAmounts, totalAmount);
    }
    return allocateEqualSplitCapped(Math.min(discount.amount, totalAmount), unitAmounts);
};
exports.allocateFullOrderDiscount = allocateFullOrderDiscount;
const allocateProRata = (totalDiscount, unitAmounts, totalAmount) => {
    const shares = unitAmounts.map((amount) => Math.floor((totalDiscount * amount) / totalAmount));
    let missingCents = totalDiscount - shares.reduce((sum, share) => sum + share, 0);
    const indexesByRemainder = unitAmounts
        .map((amount, index) => ({ index, remainder: (totalDiscount * amount) % totalAmount }))
        .filter(({ remainder }) => remainder > 0)
        .sort((a, b) => b.remainder - a.remainder);
    for (const { index } of indexesByRemainder) {
        if (missingCents === 0) {
            break;
        }
        shares[index] += 1;
        missingCents -= 1;
    }
    return shares;
};
const allocateEqualSplitCapped = (totalDiscount, unitAmounts) => {
    const shares = unitAmounts.map(() => 0);
    let remaining = totalDiscount;
    let openIndexes = unitAmounts.flatMap((amount, index) => (amount > 0 ? [index] : []));
    while (remaining > 0 && openIndexes.length > 0) {
        const toDistribute = remaining;
        const partsCount = openIndexes.length;
        openIndexes.forEach((unitIndex, position) => {
            const equalPart = (0, exports.divideAmountAcrossParts)(toDistribute, partsCount, position);
            const granted = Math.min(equalPart, unitAmounts[unitIndex] - shares[unitIndex]);
            shares[unitIndex] += granted;
            remaining -= granted;
        });
        openIndexes = openIndexes.filter((unitIndex) => shares[unitIndex] < unitAmounts[unitIndex]);
    }
    return shares;
};
const calculateWhenPerTicketDiscount = (params) => {
    const { discount, rate, attendeeIndex = 0, ticketAmount: providedTicketAmount } = params;
    if (rate.isPayWhatYouCan || rate.isAddOn) {
        return { discountId: null, discountAmount: 0 };
    }
    const attendeeCount = rate.attendeesByTicket || 1;
    // single tickets
    if (attendeeCount === 1) {
        const ticketAmount = providedTicketAmount ?? rate.amount;
        return (0, exports.calculateSingleAttendeeDiscount)(discount, ticketAmount);
    }
    return (0, exports.calculateGroupTicketDiscount)({
        discount,
        rate,
        attendeeCount,
        attendeeIndex,
        ticketAmount: providedTicketAmount,
    });
};
exports.calculateWhenPerTicketDiscount = calculateWhenPerTicketDiscount;
const calculateSingleAttendeeDiscount = (discount, ticketAmount) => {
    if (discount.type === ticketing_1.DiscountType.Dollars) {
        const discountAmount = Math.min(discount.amount, ticketAmount);
        return {
            discountId: discount.id,
            discountAmount,
        };
    }
    const discountAmount = Math.floor((ticketAmount * discount.amount) / 100);
    return {
        discountId: discount.id,
        discountAmount,
    };
};
exports.calculateSingleAttendeeDiscount = calculateSingleAttendeeDiscount;
const calculateGroupTicketDiscount = (params) => {
    const { discount, rate, attendeeCount, attendeeIndex, ticketAmount } = params;
    const attendeeAmount = ticketAmount ?? (0, exports.divideAmountAcrossParts)(rate.amount, attendeeCount, attendeeIndex);
    return (0, exports.calculateSingleAttendeeDiscount)(discount, attendeeAmount);
};
exports.calculateGroupTicketDiscount = calculateGroupTicketDiscount;
const divideAmountAcrossParts = (totalAmount, partsCount, partIndex) => {
    const basePerPart = Math.floor(totalAmount / partsCount);
    const remainder = totalAmount % partsCount;
    return partIndex < remainder ? basePerPart + 1 : basePerPart;
};
exports.divideAmountAcrossParts = divideAmountAcrossParts;
// Function used to calculate the total discount for an order before the order is created (to display discount amount in step2)
const calculateTotalDiscountForOrder = (params) => {
    const { discount, tickets, rates, discountRateMappings = [] } = params;
    if (!discount || tickets.length === 0 || rates.length === 0) {
        return 0;
    }
    if (discount.scope === ticketing_1.DiscountScope.FullOrder) {
        return (0, exports.calculateOrderFullOrderDiscount)(discount, tickets, rates);
    }
    return calculateOrderPerTicketDiscount(discount, tickets, rates, discountRateMappings);
};
exports.calculateTotalDiscountForOrder = calculateTotalDiscountForOrder;
const findRateForTicket = (rates, rateId) => {
    const direct = rates.find((r) => r.id === rateId);
    if (direct) {
        return direct;
    }
    return rates.find((r) => r.childRateIds?.includes(rateId));
};
const calculateTotalOrderAmount = (tickets, rates) => {
    return tickets.reduce((total, ticket) => {
        const rate = findRateForTicket(rates, ticket.rateId);
        if (!rate || rate.isPayWhatYouCan) {
            return total;
        }
        return total + (ticket.purchasedNumber || 0) * rate.amount;
    }, 0);
};
exports.calculateTotalOrderAmount = calculateTotalOrderAmount;
const calculateOrderFullOrderDiscount = (discount, tickets, rates) => {
    const totalOrderAmount = (0, exports.calculateTotalOrderAmount)(tickets, rates);
    if (discount.type === ticketing_1.DiscountType.Dollars) {
        return Math.min(discount.amount, totalOrderAmount);
    }
    return Math.floor((totalOrderAmount * discount.amount) / 100);
};
exports.calculateOrderFullOrderDiscount = calculateOrderFullOrderDiscount;
const calculateOrderPerTicketDiscount = (discount, tickets, rates, discountRateMappings) => {
    return tickets.reduce((totalDiscount, ticket) => {
        const rate = findRateForTicket(rates, ticket.rateId);
        if (!rate) {
            return totalDiscount;
        }
        const isEligible = (0, discount_helpers_1.isRateEligibleForDiscount)({
            scope: discount.scope,
            rateId: rate.id,
            discountRateMappings,
            isPayWhatYouCan: Boolean(rate.isPayWhatYouCan),
            isAddOn: Boolean(rate.isAddOn),
            parentRateId: rate.parentRateId,
        });
        if (!isEligible) {
            return totalDiscount;
        }
        const attendeeCount = rate.attendeesByTicket || 1;
        const purchasedNumber = ticket.purchasedNumber || 0;
        const ticketDiscount = calculateDiscountForSingleTicketInOrder(discount, rate, attendeeCount);
        return totalDiscount + purchasedNumber * ticketDiscount;
    }, 0);
};
const calculateDiscountForSingleTicketInOrder = (discount, rate, attendeeCount) => {
    if (rate.isPayWhatYouCan) {
        return 0;
    }
    return Array.from({ length: attendeeCount }, (_, attendeeIdx) => {
        const result = (0, exports.calculateDiscountPerProductTicket)({
            discount,
            rate,
            attendeeIndex: attendeeIdx,
        });
        return result.discountAmount;
    }).reduce((sum, discountAmount) => sum + discountAmount, 0);
};
const calculateTicketLineDiscount = (params) => {
    const { discount, discountRateMappings, rate, ticketRateId, unitAmount } = params;
    if (!discount || rate.isPayWhatYouCan) {
        return 0;
    }
    if (discount.scope === ticketing_1.DiscountScope.FullOrder) {
        return params.fullOrderUnitDiscountAmount;
    }
    const isEligible = (0, discount_helpers_1.isRateEligibleForDiscount)({
        scope: discount.scope,
        rateId: ticketRateId,
        parentRateId: rate.parentRateId ?? (rate.id !== ticketRateId ? rate.id : undefined),
        discountRateMappings,
        isPayWhatYouCan: Boolean(rate.isPayWhatYouCan),
        isAddOn: Boolean(rate.isAddOn),
    });
    if (!isEligible) {
        return 0;
    }
    const attendeeCount = rate.attendeesByTicket || 1;
    return Array.from({ length: attendeeCount }, (_, attendeeIndex) => {
        const attendeeAmount = (0, exports.divideAmountAcrossParts)(unitAmount, attendeeCount, attendeeIndex);
        return (0, exports.calculateSingleAttendeeDiscount)(discount, attendeeAmount).discountAmount;
    }).reduce((sum, amount) => sum + amount, 0);
};
exports.calculateTicketLineDiscount = calculateTicketLineDiscount;
const calculateOrderDiscountAmount = (params) => {
    const { discount, tickets, rates, parentRates = [], discountRateMappings = [] } = params;
    const childRateIdsByParentId = new Map();
    const topLevelRates = new Map();
    for (const rate of [...rates, ...parentRates]) {
        if (rate.parentRateId) {
            childRateIdsByParentId.set(rate.parentRateId, (childRateIdsByParentId.get(rate.parentRateId) ?? new Set()).add(rate.id));
        }
        else {
            topLevelRates.set(rate.id, rate);
        }
    }
    return (0, exports.calculateTotalDiscountForOrder)({
        discount: discount,
        tickets,
        rates: [...topLevelRates.values()].map((rate) => {
            const childRateIds = [...new Set([...(rate.childRateIds ?? []), ...(childRateIdsByParentId.get(rate.id) ?? [])])];
            return {
                id: rate.id,
                amount: rate.amount,
                attendeesByTicket: rate.attendeesByTicket ?? undefined,
                isPayWhatYouCan: Boolean(rate.isPayWhatYouCan),
                isAddOn: Boolean(rate.isAddOn),
                childRateIds: childRateIds.length > 0 ? childRateIds : undefined,
            };
        }),
        discountRateMappings,
    });
};
exports.calculateOrderDiscountAmount = calculateOrderDiscountAmount;
